import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { Dispatch, SetStateAction, useContext, useEffect } from "react";
import { OrganizationNiche, TemplateError } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import FollowUpDelayDivider from "components/FollowUpDelayDivider";
import TemplateEditButton from "components/TemplateEditButton";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import {
  DAYS_TO_DELAY_FOLLOWUP,
  MAXIMUM_FOLLOWUP_ALLOWED,
} from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { trackEvent } from "utils/tracking";
import { useRegisterSaveAsNew } from "utils/useRegisterSaveAsNew";

const styles = {
  createButton: {
    backgroundColor: "primary.main",
    color: "white",
    borderRadius: "12px",
    fontSize: 12,
    px: 1,
    py: 0.5,
  },
};

interface Props {
  modifiedTemplates: Template[];
  setModifiedTemplates: Dispatch<SetStateAction<Template[]>>;
  modifiedTemplateDelayDays: (number | null)[];
  setModifiedTemplateDelayDays: Dispatch<SetStateAction<(number | null)[]>>;
  templatesError: TemplateError | null;
  nicheTemplates?: Template[];
  niche?: OrganizationNiche;
  loadData?: () => void;
}

const EditNicheTemplate = ({
  modifiedTemplates,
  setModifiedTemplates,
  modifiedTemplateDelayDays,
  setModifiedTemplateDelayDays,
  templatesError,
  nicheTemplates,
  niche,
  loadData,
}: Props) => {
  const { emailSettings } = useContext(OrganizationUserContext);
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    setCreateTemplateDialogOpen,
    fetchOutreachTemplates,
    setCurrentTemplateContentType,
  } = useContext(OutreachTemplatesContext);

  useRegisterSaveAsNew((newTemplate, editedId) =>
    setModifiedTemplates((prev) =>
      prev.map((t) => (t.id === editedId ? newTemplate : t)),
    ),
  );

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );

  const initializeTemplates = () => {
    if (nicheTemplates && nicheTemplates.length > 0) {
      setModifiedTemplates(nicheTemplates);
      return;
    }

    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }

    const presetTemplates = emailSettings?.emailSettingTemplates || [];
    const matchedTemplates: Template[] = [];

    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) {
        matchedTemplates.push(template);
      }
    }
    if (matchedTemplates?.length > 0) {
      setModifiedTemplates(matchedTemplates);
      return;
    }

    if (filteredOutreachTemplates?.length > 0) {
      setModifiedTemplates([filteredOutreachTemplates[0]]);
    } else if (filteredOutreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    }
  };

  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const selectedFollowups = modifiedTemplates?.slice(
    1,
    modifiedTemplates?.length,
  );

  const handleGenerateTemplate = () => {
    trackEvent("Generate Template Button Clicked", {
      Source: "VA Add Niche",
    });
    setCurrentTemplateContentType(TemplateContentType.OUTREACH);
    setCreateTemplateDialogOpen(true);
  };

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...modifiedTemplates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = template;
      setModifiedTemplates(copy);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...modifiedTemplates];
    copy.splice(index, 1);
    setModifiedTemplates(copy);
    const delayCopy = [...modifiedTemplateDelayDays];
    delayCopy.splice(index, 1);
    setModifiedTemplateDelayDays(delayCopy);
  };

  const addFollowUp = () => {
    setModifiedTemplates([...modifiedTemplates, followupTemplates?.[0]]);
    setModifiedTemplateDelayDays([
      ...modifiedTemplateDelayDays,
      DAYS_TO_DELAY_FOLLOWUP,
    ]);
  };

  const handleDelayChange = (index: number, value: number) => {
    const copy = [...modifiedTemplateDelayDays];
    copy[index] = value;
    setModifiedTemplateDelayDays(copy);
  };

  useEffect(() => {
    if (modifiedTemplates.length === 0) {
      initializeTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates?.length]);

  return (
    <>
      <Box>
        <Typography variant="h6" paragraph>
          Let's set up your outreach template 🚀
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        {modifiedTemplates[0] ? (
          <OutreachSelect
            templates={modifiedTemplates}
            handleChange={handleTemplateChange}
            filteredOutreachTemplates={filteredOutreachTemplates}
            templateFromEmailSettings={templateFromEmailSettings}
            addNewOption={handleGenerateTemplate}
            nicheId={niche?.id}
          />
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={handleGenerateTemplate}
            sx={{ height: 25, mt: 0.4, textTransform: "none" }}
            disableElevation
          >
            Generate Template
          </Button>
        )}
        {templatesError && templatesError.idx === 0 && (
          <Grid container>
            <Typography
              variant="caption"
              sx={{ fontStyle: "italic", mt: 0.5, color: "error.main" }}
            >
              {templatesError.message}
            </Typography>
          </Grid>
        )}
      </Box>
      <Box>
        <Typography variant="h6" paragraph>
          Let's set up your follow-ups
        </Typography>
      </Box>
      <Grid container sx={{ mb: 4 }} columnSpacing={{ xs: 0, sm: 0.5 }}>
        <Grid item xs={12}>
          {selectedFollowups?.map((followup, index) => (
            <Grid container key={index}>
              <Grid container item xs={12} justifyContent="flex-start">
                <FollowUpDelayDivider
                  days={
                    modifiedTemplateDelayDays[index + 1] ??
                    DAYS_TO_DELAY_FOLLOWUP
                  }
                  onChange={(val) => handleDelayChange(index + 1, val)}
                  isFirst={index === 0}
                />
              </Grid>
              <Grid container alignItems="center" gap={1} item xs={12}>
                <Select
                  onChange={(e) => {
                    e.stopPropagation();
                    handleTemplateChange(e, index + 1);
                  }}
                  onMouseUp={(e) => {
                    e.stopPropagation();
                  }}
                  value={followup?.id}
                  size="small"
                >
                  <MenuItem
                    onClick={() => {
                      setCurrentTemplateContentType(
                        TemplateContentType.FOLLOWUP,
                      );
                      setCreateTemplateDialogOpen(true);
                    }}
                  >
                    <Box sx={styles.createButton}>Create New</Box>
                  </MenuItem>
                  {filteredFollowUps?.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Typography noWrap sx={{ maxWidth: 225 }}>
                          {template.name}
                        </Typography>
                        {template.id &&
                          templateFromEmailSettings?.includes(template.id) && (
                            <Chip
                              label="Last Used"
                              size="small"
                              sx={{
                                ml: 1,
                                fontSize: 10,
                                backgroundColor: "primary.light",
                              }}
                            />
                          )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>

                {followup?.id && (
                  <TemplateEditButton
                    sx={{ mr: -0.5 }}
                    templateId={followup.id}
                    nicheId={niche?.id}
                  />
                )}

                <IconButton
                  sx={{ fontSize: 13, color: grey[500] }}
                  onClick={() => removeFollowUp(index + 1)}
                >
                  <Box component="i" className="fa-regular fa-trash" />
                </IconButton>
              </Grid>
              {templatesError && templatesError.idx === index + 1 && (
                <Grid container>
                  <Typography
                    variant="caption"
                    sx={{
                      fontStyle: "italic",
                      mt: 0.5,
                      color: "error.main",
                    }}
                  >
                    {templatesError.message}
                  </Typography>
                </Grid>
              )}
            </Grid>
          ))}
          {followupTemplates?.length > 0 &&
            modifiedTemplates.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
              <Grid container alignItems="center">
                <Grid item xs md>
                  <Button
                    disableElevation
                    onClick={addFollowUp}
                    variant="contained"
                    sx={{ mt: 1 }}
                  >
                    + Follow Up
                  </Button>
                </Grid>
              </Grid>
            )}
          {followupTemplates.length === 0 && (
            <Button
              variant="contained"
              size="small"
              onClick={handleGenerateTemplate}
              sx={{
                height: 25,
                mt: 0.4,
                textTransform: "none",
              }}
              disableElevation
            >
              Generate Follow-up
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default EditNicheTemplate;
